import { useMemo } from 'react'

import type { ColumnDef } from '@tanstack/react-table'
import { useUnit } from 'effector-react'
import { Link } from 'react-router-dom'

import OnlyFansIcon from '@/assets/icons/icon-only-fans.svg'
import { DataTable } from '@/components/ui/table/data-table.tsx'
import { Tag } from '@/components/ui/tag.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { $creatorId, $creators } from '@/models/creators.ts'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import { LiveCreatorStatusIndicator } from '@/modules/creators/ui/LiveCreatorStatusIndicator.tsx'
import type { BillingPlan, License } from '@/modules/licenses/types.ts'

import type { Creator } from '../types'
import { CreatorStatus } from '../types'
import { CreatorActions } from '../ui/CreatorActions'
import { LicenseChanger } from '../ui/LicenseChanger'

export const CreatorsTable = ({
  searchQuery,
  plans,
  licenses,
  openPurchaseLicense,
  openSetLicense,
  openUpgradeLicense,
}: {
  searchQuery: string
  plans: BillingPlan[]
  licenses: License[]
  openPurchaseLicense: (creator: Creator, plan: BillingPlan) => void
  openSetLicense: (
    creator: Creator,
    license: License,
    plan: BillingPlan,
  ) => void
  openUpgradeLicense: (creator: Creator, plan: BillingPlan) => void
}) => {
  const allCreators = useUnit($creators)
  const creatorId = useUnit($creatorId)

  const creators = useMemo(() => {
    return allCreators
      ? allCreators.filter((creator) =>
          creator.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : []
  }, [allCreators, searchQuery])

  const mapStatusText = (status: string) => {
    switch (status) {
      case CreatorStatus.NotPaid:
        return 'Unpaid'
      case CreatorStatus.Licensable:
      case CreatorStatus.NotLicensable:
        return 'Inactive'
      default:
        return 'Active'
    }
  }

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div className="relative">
            <CreatorAvatar
              name={row.original.name}
              avatarSrc={row.original.avatar.croppedUrl}
              className="h-10 w-10"
            />
            {!isUnusableCreator(row.original) && (
              <LiveCreatorStatusIndicator creatorId={row.original.id} />
            )}
          </div>

          <div className="flex flex-col font-medium leading-[0.9rem]">
            <span className="flex items-center gap-1">
              {row.original.name}{' '}
              <Link
                to={'https://onlyfans.com/u' + row.original.onlyfansId}
                target="_blank"
              >
                <OnlyFansIcon className="text-primary-500 hover:text-primary-600 inline h-[16px] w-[16px]" />
              </Link>
            </span>
            {creatorId === row.original.id && (
              <span className="text-primary-500 text-[0.65625rem]">
                Selected
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      invertSorting: true,
      sortingFn: (a, b) => {
        const creatorStatusA = mapStatusText(
          a.original.status.length > 0 ? a.original.status[0] : 'Active',
        )
        const creatorStatusB = mapStatusText(
          b.original.status.length > 0 ? b.original.status[0] : 'Active',
        )

        return creatorStatusA.localeCompare(creatorStatusB)
      },
      cell: ({ row }) => {
        const creatorStatus =
          row.original.status.length > 0 ? row.original.status[0] : 'Active'

        const mapTooltipText = (status: string) => {
          switch (status) {
            case CreatorStatus.NotPaid:
              return 'Please pay the open invoice to re-activate this creator.'
            case CreatorStatus.Licensable:
            case CreatorStatus.NotLicensable:
              return 'Select a license to activate the creator'
            default:
              return null
          }
        }

        const badge = (
          <Tag
            withDot={true}
            variant={isUnusableCreator(row.original) ? 'danger' : 'success'}
          >
            {mapStatusText(creatorStatus)}
          </Tag>
        )
        const tooltipText = mapTooltipText(creatorStatus)

        if (tooltipText) {
          return <Tooltip content={tooltipText}>{badge}</Tooltip>
        }

        return badge
      },
    },
    {
      // only adding this so canSort is true...
      accessorKey: 'plan',
      header: 'License',
      enableSorting: true,
      sortingFn: (a, b) => {
        const planA =
          (a.original.plan != null
            ? plans.find((plan) => a.original.plan.startsWith(plan.name))
                ?.displayName
            : null) ?? 'No license'
        const planB =
          (b.original.plan != null
            ? plans.find((plan) => b.original.plan.startsWith(plan.name))
                ?.displayName
            : null) ?? 'No license'

        if (planA === 'No license' && planB === 'No license') {
          return 0
        }

        if (planA === 'No license') {
          return -1
        }

        if (planB === 'No license') {
          return 1
        }

        return planA.localeCompare(planB)
      },
      cell: ({ row }) => (
        <LicenseChanger
          row={row}
          plans={plans}
          licenses={licenses}
          openPurchaseLicense={openPurchaseLicense}
          openSetLicense={openSetLicense}
          openUpgradeLicense={openUpgradeLicense}
        />
      ),
    },
    {
      id: 'actions',
      header: () => (
        <div className="w-full">
          <span className="float-right">Actions</span>
        </div>
      ),
      cell: ({ row }) => <CreatorActions row={row} />,
    },
  ]

  return (
    <DataTable
      enableSorting={true}
      enableSortingRemoval={true}
      data={creators}
      columns={columns}
      sortingState={[{ id: 'name', desc: false }]}
    />
  )
}

const isUnusableCreator = (creator: Creator) =>
  creator.status.length > 0 &&
  [
    CreatorStatus.NotPaid,
    CreatorStatus.Licensable,
    CreatorStatus.NotLicensable,
  ].includes(creator.status[0])
